import { fonts } from '@/constants/fonts';
import { useThemeColor } from '@/hooks/useThemeColor';
import React from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import ThemedText from './ThemedText';

type DividerOrientation = 'horizontal' | 'vertical';
type DividerVariant = 'solid' | 'dashed' | 'dotted';

interface DividerProps {
  orientation?: DividerOrientation;
  variant?: DividerVariant;
  label?: string;
  labelPosition?: 'left' | 'center' | 'right';
  thickness?: number;
  color?: string;
  spacing?: number;
  style?: ViewStyle;
}

export default function Divider({
  orientation = 'horizontal',
  variant = 'solid',
  label,
  labelPosition = 'center',
  thickness = 1,
  color,
  spacing = 16,
  style,
}: DividerProps) {
  const defaultColor = useThemeColor({}, 'border');
  const textSecondary = useThemeColor({}, 'textSecondary');
  const dividerColor = color || defaultColor;

  const isHorizontal = orientation === 'horizontal';

  // Base divider styles
  const dividerStyle: ViewStyle = {
    backgroundColor: variant === 'solid' ? dividerColor : 'transparent',
    ...(isHorizontal
      ? {
          height: thickness,
          width: '100%',
          marginVertical: spacing,
        }
      : {
          width: thickness,
          height: '100%',
          marginHorizontal: spacing,
        }),
  };

  // Dashed/Dotted styles
  if (variant === 'dashed' || variant === 'dotted') {
    dividerStyle.borderColor = dividerColor;
    dividerStyle.backgroundColor = 'transparent';

    if (isHorizontal) {
      dividerStyle.borderTopWidth = thickness;
      dividerStyle.borderStyle = variant;
    } else {
      dividerStyle.borderLeftWidth = thickness;
      dividerStyle.borderStyle = variant;
    }
  }

  // Without label - simple divider
  if (!label) {
    return <View style={[dividerStyle, style]} />;
  }

  // With label - horizontal only
  if (!isHorizontal) {
    console.warn('Divider: Labels are only supported with horizontal orientation');
    return <View style={[dividerStyle, style]} />;
  }

  // Horizontal divider with label
  const renderDividerLine = (flex: number = 1) => (
    <View
      style={[
        dividerStyle,
        {
          flex,
          marginVertical: 0,
        },
      ]}
    />
  );

  const getFlexValues = () => {
    switch (labelPosition) {
      case 'left':
        return { left: 0.3, right: 1 };
      case 'right':
        return { left: 1, right: 0.3 };
      case 'center':
      default:
        return { left: 1, right: 1 };
    }
  };

  const flexValues = getFlexValues();

  return (
    <View
      style={[
        styles.labelContainer,
        {
          marginVertical: spacing,
        },
        style,
      ]}
    >
      {renderDividerLine(flexValues.left)}
      <ThemedText
        style={[
          styles.label,
          {
            color: textSecondary,
            marginHorizontal: 12,
          },
        ]}
      >
        {label}
      </ThemedText>
      {renderDividerLine(flexValues.right)}
    </View>
  );
}

const styles = StyleSheet.create({
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontSize: 12,
    fontFamily: fonts.medium,
    textTransform: 'uppercase',
  },
});
